/**
 * Date formatting helpers — pure presentation, not domain math. Deriving
 * "is this overdue / how many days away" stays in the domain layer
 * (DueActionView.isOverdue, ObjectiveForecastView); this module only turns
 * already-derived dates into display strings.
 */

const WEEKDAY = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
];
const MONTH_TITLE = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "TUE · JUL 14" — today-view.md header kicker. */
export function formatKicker(date: Date): string {
  return `${WEEKDAY[date.getDay()]} · ${MONTH[date.getMonth()]} ${date.getDate()}`;
}

/**
 * Device-local calendar date as 'YYYY-MM-DD' (ADR-0006: "the user's day is
 * wherever the user is" — device-local, never UTC). Mirrors
 * `src/domain/clock/system-clock.ts`'s `toIsoDate` exactly (that module is
 * the domain layer's own local-date source; this is the UI-side equivalent
 * for the one place a component needs "today" before any domain query has
 * run — see TodayView.tsx's DOMAIN-API-GAP comment).
 *
 * Bug fixed by this function's existence: `new Date().toISOString().slice(0,
 * 10)` reads UTC, not local time — in any timezone behind UTC, that
 * silently returns TOMORROW'S date during the evening (verified live: in
 * MDT (UTC-6), at 22:06 local on 2026-07-02, `toISOString()` already reads
 * "2026-07-03Z"). That mismatch made every habit check-in command reject
 * with "date must be today or yesterday", since the domain layer's own
 * `ClockPort` (system-clock.ts) correctly returns local-time "today" while
 * this component was passing UTC-tomorrow.
 */
export function localIsoDate(date: Date = new Date()): string {
  const year = date.getFullYear().toString().padStart(4, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** "Nov 05" — objective-detail.md subtitle date. */
export function formatShortDate(isoDate: string): string {
  const [, m, d] = isoDate.split('-').map(Number);
  return `${MONTH_TITLE[(m ?? 1) - 1]} ${String(d).padStart(2, '0')}`;
}

/**
 * "TOMORROW" / "TODAY" relative pill for a dated action (today-view.md
 * SS1.4). Undated actions render no pill (rollup takes that slot instead —
 * handled by the caller). Dates further than tomorrow render the short date
 * rather than a fabricated relative word, per the "never fabricate" rule.
 */
export function formatRelativeDatePill(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${isoDate}T00:00:00`);
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86_400_000);

  if (diffDays === 0) return 'TODAY';
  if (diffDays === 1) return 'TOMORROW';
  return formatShortDate(isoDate).toUpperCase();
}

/** "July 2026" — completion-history.md §1.3's month nav header, from a 'YYYY-MM' YearMonth. */
export function formatMonthYear(yearMonth: string): string {
  const [y, m] = yearMonth.split('-').map(Number);
  const fullMonth = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${fullMonth[(m ?? 1) - 1]} ${y}`;
}

/** "Jul" — completion-history.md §1.2's year-strip chip label, from a 'YYYY-MM' YearMonth. */
export function formatMonthAbbrev(yearMonth: string): string {
  const [, m] = yearMonth.split('-').map(Number);
  return MONTH_TITLE[(m ?? 1) - 1] ?? '';
}

/** The adjacent 'YYYY-MM' YearMonth, +1/-1 months from `yearMonth`. */
export function shiftYearMonth(yearMonth: string, delta: number): string {
  const [y, m] = yearMonth.split('-').map(Number);
  const zeroBased = (m ?? 1) - 1 + delta;
  const nextYear = (y ?? 1970) + Math.floor(zeroBased / 12);
  const nextMonth = ((zeroBased % 12) + 12) % 12;
  return `${String(nextYear).padStart(4, '0')}-${String(nextMonth + 1).padStart(2, '0')}`;
}

/**
 * "Jul 7 – 13" — a Mon-Sun week's range (desktop-weekly-review.md §1.1
 * header). Same month/day-numeral shape as formatShortDate but the end date
 * drops the repeated month name when both dates share a month (the common
 * case for a 7-day span); spans a month boundary render each date's own
 * month ("Jun 29 – Jul 5").
 */
export function formatWeekRange(weekStart: string): string {
  const [, startM, startD] = weekStart.split('-').map(Number);
  const sundayDate = new Date(`${weekStart}T00:00:00`);
  sundayDate.setDate(sundayDate.getDate() + 6);
  const endM = sundayDate.getMonth() + 1;
  const endD = sundayDate.getDate();

  const startLabel = `${MONTH_TITLE[(startM ?? 1) - 1]} ${startD}`;
  const endLabel =
    startM === endM ? `${endD}` : `${MONTH_TITLE[endM - 1]} ${endD}`;
  return `${startLabel} – ${endLabel}`;
}
